import { Toast } from '../../../../components/Common/Toast';
import { HTTPStatusEnum } from '../../../../interfaces/log.interface';
import { callAPI } from '../../../../services/api.service';
import { TokenCookieManager } from '../../../../utils/tokenCookies';
import { getImpersonationToken, shouldUseImpersonationAuth } from '../../../../utils/impersonation.utils';
import { sseUpload, type ImportProgressData } from '../../../../utils/sseUpload';

export interface SchoolModel {
  id: string;
  name: string;
  nameLower: string;
  externalId?: string;
  stateSchoolId?: string;
  districtId?: string;
  districtName?: string;
  cleverId?: string;
  country: string;
  state: string;
  city?: string;
  zip?: string;
  address?: string;
  phone?: string;
  website?: string;
  schoolType?: string;
  gradeRange?: { low: string; high: string };
  level?: string;
  enrollment?: number;
  source: string;
  sourcePriority: number;
  status?: string;
  isActive: boolean;
  createdAt?: any;
  updatedAt?: any;
}

export const getAdminSchools = async (
  setSchools: (schools: SchoolModel[]) => void,
  setTotalPages: (totalPages: number) => void,
  page?: number,
  state?: string,
  search?: string,
  source?: string,
  setTotalRecords?: (totalRecords: number) => void,
  country?: string,
) => {
  try {
    const params = new URLSearchParams();
    if (page) params.set('page', String(page));
    if (state) params.set('state', state);
    if (search) params.set('search', search);
    if (source) params.set('source', source);
    if (country) params.set('country', country);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await callAPI(`admin/school${qs}`);
    if (response?.schools) {
      setSchools(response.schools);
      setTotalPages(response.totalPages ?? 0);
      setTotalRecords?.(response.totalRecords ?? 0);
    } else {
      Toast('Failed to get schools', true);
    }
  } catch (error) {
    Toast('Failed to get schools', true);
  }
};

export const saveSchool = async (
  data: Partial<SchoolModel>,
  onSuccess: () => void
) => {
  try {
    const response = await callAPI('admin/school/save', data);
    if (response?.status === HTTPStatusEnum.OK) {
      Toast('School created successfully');
      onSuccess();
    } else {
      Toast(response?.message || 'Failed to create school', true);
    }
  } catch (error) {
    Toast('Failed to create school', true);
  }
};

export const updateSchool = async (
  data: Partial<SchoolModel> & { id: string },
  onSuccess: () => void
) => {
  try {
    const response = await callAPI('admin/school/update', data);
    if (response?.status === HTTPStatusEnum.OK) {
      Toast('School updated successfully');
      onSuccess();
    } else {
      Toast(response?.message || 'Failed to update school', true);
    }
  } catch (error) {
    Toast('Failed to update school', true);
  }
};

export const deleteSchool = async (id: string, onSuccess: () => void) => {
  try {
    const response = await callAPI('admin/school/delete', { id });
    if (response?.status === HTTPStatusEnum.OK) {
      Toast('School deleted successfully');
      onSuccess();
    } else {
      Toast(response?.message || 'Failed to delete school', true);
    }
  } catch (error) {
    Toast('Failed to delete school', true);
  }
};

export const changeSchoolStatus = async (id: string, onSuccess: () => void) => {
  try {
    const response = await callAPI('admin/school/status', { id });
    if (response?.status === HTTPStatusEnum.OK) {
      Toast('School status updated successfully');
      onSuccess();
    } else {
      Toast(response?.message || 'Failed to update school status', true);
    }
  } catch (error) {
    Toast('Failed to update school status', true);
  }
};

const uploadSchoolsCsv = async (
  endpoint: string,
  file: File,
  label: string,
  onProgress?: (data: ImportProgressData) => void,
): Promise<{ success: boolean; message: string }> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};
    if (shouldUseImpersonationAuth()) {
      const token = getImpersonationToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }

    return await sseUpload(
      `${process.env.REACT_APP_API_URL}/${endpoint}`,
      formData,
      onProgress,
      headers,
    );
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      return { success: false, message: `Import timed out. The file may be too large or the server is busy. Please try again.` };
    }
    return { success: false, message: `Network error while importing ${label.toLowerCase()}. Please check your connection and try again.` };
  }
};

export const importPublicSchoolsCsv = async (
  file: File,
  onProgress?: (data: ImportProgressData) => void,
): Promise<{ success: boolean; message: string }> => {
  return uploadSchoolsCsv('admin/school/import/public', file, 'Public schools', onProgress);
};

export const importPrivateSchoolsCsv = async (
  file: File,
  onProgress?: (data: ImportProgressData) => void,
): Promise<{ success: boolean; message: string }> => {
  return uploadSchoolsCsv('admin/school/import/private', file, 'Private schools', onProgress);
};

export const deleteAllSchools = async (): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await callAPI('admin/school/delete-all', null);
    if (response?.status === HTTPStatusEnum.OK) {
      return { success: true, message: response.message || 'All schools deleted' };
    } else {
      return { success: false, message: response?.message || 'Failed to delete all schools' };
    }
  } catch (error) {
    return { success: false, message: 'Failed to delete all schools' };
  }
};

// ── Teacher Queries ─────────────────────────────────────────────────────

export interface TeacherListItem {
  email: string;
  firstName?: string;
  lastName?: string;
  joinedAt?: any;
}

export const getTeacherCountsBySchoolIds = async (
  schoolIds: string[]
): Promise<Record<string, number>> => {
  try {
    const response = await callAPI('admin/school/teacher-counts', { schoolIds });
    if (response?.status === HTTPStatusEnum.OK) {
      return response.data || {};
    }
    return {};
  } catch (error) {
    console.error('Failed to fetch teacher counts:', error);
    return {};
  }
};

export const getSchoolTeachers = async (
  schoolId: string,
  page: number = 1,
  limit: number = 25,
  search?: string
): Promise<{ teachers: TeacherListItem[]; totalPages: number }> => {
  try {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (search) params.set('search', search);
    const response = await callAPI(`admin/school/${schoolId}/teachers?${params.toString()}`, null, 'GET');
    if (response?.status === HTTPStatusEnum.OK) {
      return {
        teachers: response.teachers || [],
        totalPages: response.totalPages ?? 0,
      };
    }
    return { teachers: [], totalPages: 0 };
  } catch (error) {
    console.error('Failed to fetch school teachers:', error);
    return { teachers: [], totalPages: 0 };
  }
};
